import { useI18n } from 'locales'
import { formatAmount } from 'utils/currency'
import { computeLineItemAmountCents } from 'utils/invoice-helpers'

import {
  InvoiceLineItemsSection,
  InvoiceLineItemsTable,
  InvoiceLineItemsHeaderCellDescription,
  InvoiceLineItemsHeaderCellRight,
  InvoiceLineItemsRow,
  InvoiceLineItemsCellDescription,
  InvoiceLineItemsCell,
  InvoiceLineItemsCellRight,
  InvoiceLineItemName,
  InvoiceLineItemDescription,
  InvoiceLineItemMobileMeta,
  InvoiceLineItemMobileMetaItem,
  InvoiceLineItemNameRow,
  InvoiceLineItemAmount,
  InvoiceTotalsSection,
  InvoiceTotalsRow,
  InvoiceTotalsRowDiscount,
  InvoiceTotalsRowTotal,
  InvoiceTotalsLabel,
  InvoiceTotalsValue,
  InvoiceNotesSection,
  InvoiceNotesLabel,
  InvoiceNotesBody,
} from './styles'

const InvoiceLineItems = ({
  lineItems,
  currency,
  isPhone,
  subtotalCents,
  discountTotalCents,
  discountLabel,
  taxTotalCents,
  totalCents,
  parsedTaxRate,
  taxName,
  showTaxRow,
  invoiceNotes,
  isVoided,
}) => {
  const { i18n } = useI18n()

  return (
    <InvoiceLineItemsSection>
      <InvoiceLineItemsTable>
        <thead>
          <tr>
            <InvoiceLineItemsHeaderCellDescription>
              {isPhone
                ? i18n.t('booking.invoice.invoice_details')
                : i18n.t('booking.invoice.th_description')}
            </InvoiceLineItemsHeaderCellDescription>
            {!isPhone ? (
              <>
                <InvoiceLineItemsHeaderCellRight>
                  {i18n.t('booking.invoice.th_qty')}
                </InvoiceLineItemsHeaderCellRight>
                <InvoiceLineItemsHeaderCellRight>
                  {i18n.t('booking.invoice.th_price')}
                </InvoiceLineItemsHeaderCellRight>
                <InvoiceLineItemsHeaderCellRight>
                  {i18n.t('booking.invoice.th_amount')}
                </InvoiceLineItemsHeaderCellRight>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {lineItems.length > 0 ? (
            lineItems.map((lineItem) => {
              const lineAmountCents = computeLineItemAmountCents(lineItem)
              return (
                <InvoiceLineItemsRow key={lineItem.uuid}>
                  <InvoiceLineItemsCellDescription>
                    {isPhone ? (
                      <InvoiceLineItemNameRow>
                        <InvoiceLineItemName>{lineItem.name}</InvoiceLineItemName>
                        <InvoiceLineItemAmount>
                          {formatAmount(lineAmountCents / 100, currency)}
                        </InvoiceLineItemAmount>
                      </InvoiceLineItemNameRow>
                    ) : (
                      <InvoiceLineItemName>{lineItem.name}</InvoiceLineItemName>
                    )}
                    {isPhone ? (
                      <InvoiceLineItemMobileMeta>
                        <InvoiceLineItemMobileMetaItem>
                          {`${lineItem.quantity} · ${formatAmount(
                            (lineItem.unit_price_cents || 0) / 100,
                            currency,
                          )}`}
                        </InvoiceLineItemMobileMetaItem>
                      </InvoiceLineItemMobileMeta>
                    ) : null}
                    {lineItem.description ? (
                      <InvoiceLineItemDescription>
                        {lineItem.description}
                      </InvoiceLineItemDescription>
                    ) : null}
                  </InvoiceLineItemsCellDescription>
                  {!isPhone ? (
                    <>
                      <InvoiceLineItemsCellRight>
                        {lineItem.quantity}
                      </InvoiceLineItemsCellRight>
                      <InvoiceLineItemsCellRight>
                        {formatAmount((lineItem.unit_price_cents || 0) / 100, currency)}
                      </InvoiceLineItemsCellRight>
                      <InvoiceLineItemsCellRight>
                        {formatAmount(lineAmountCents / 100, currency)}
                      </InvoiceLineItemsCellRight>
                    </>
                  ) : null}
                </InvoiceLineItemsRow>
              )
            })
          ) : (
            <InvoiceLineItemsRow>
              <InvoiceLineItemsCell colSpan={isPhone ? 1 : 4}>
                {i18n.t('booking.invoice.no_line_items')}
              </InvoiceLineItemsCell>
            </InvoiceLineItemsRow>
          )}
        </tbody>
      </InvoiceLineItemsTable>

      <InvoiceTotalsSection>
        <InvoiceTotalsRow>
          <InvoiceTotalsLabel>{i18n.t('booking.invoice.subtotal')}</InvoiceTotalsLabel>
          <InvoiceTotalsValue>
            {formatAmount(subtotalCents / 100, currency)}
          </InvoiceTotalsValue>
        </InvoiceTotalsRow>
        {discountTotalCents > 0 ? (
          <InvoiceTotalsRowDiscount>
            <InvoiceTotalsLabel>{discountLabel}</InvoiceTotalsLabel>
            <InvoiceTotalsValue>
              -
              {formatAmount(discountTotalCents / 100, currency)}
            </InvoiceTotalsValue>
          </InvoiceTotalsRowDiscount>
        ) : null}
        {showTaxRow ? (
          <InvoiceTotalsRow>
            <InvoiceTotalsLabel>
              {i18n.t('booking.invoice.tax_with_rate', {
                tax_name: taxName,
                rate: parsedTaxRate.toFixed(2),
              })}
            </InvoiceTotalsLabel>
            <InvoiceTotalsValue>
              {formatAmount(taxTotalCents / 100, currency)}
            </InvoiceTotalsValue>
          </InvoiceTotalsRow>
        ) : null}
        <InvoiceTotalsRowTotal>
          <InvoiceTotalsLabel>{i18n.t('booking.invoice.amount_due')}</InvoiceTotalsLabel>
          <InvoiceTotalsValue>{formatAmount(totalCents / 100, currency)}</InvoiceTotalsValue>
        </InvoiceTotalsRowTotal>
      </InvoiceTotalsSection>
      {invoiceNotes && !isVoided ? (
        <InvoiceNotesSection>
          <InvoiceNotesLabel>{i18n.t('booking.invoice.notes')}</InvoiceNotesLabel>
          <InvoiceNotesBody>{invoiceNotes}</InvoiceNotesBody>
        </InvoiceNotesSection>
      ) : null}
    </InvoiceLineItemsSection>
  )
}

export default InvoiceLineItems
